import { syncAccountPartner } from '../config/partnerThemes';
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import API, { endpoints } from '../services/api_config';

const ageFrom = (iso) => {
  if (!iso) return null;
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return null;
  const now = new Date();
  let age = now.getFullYear() - y;
  if (now.getMonth() + 1 < m || (now.getMonth() + 1 === m && now.getDate() < d)) age--;
  return age;
};
const ageBlocked = () => { try { return sessionStorage.getItem('fho_age_block') === '1'; } catch (e) { return false; } };

const Register = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    full_name: '',
    birthdate: '',
    gender: '',
    referral_source: '',
    partner_code: '',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York'
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const navigate = useNavigate();
  const [guardianOk, setGuardianOk] = useState(false);
  const age = ageFrom(formData.birthdate);
  const isMinor = age !== null && age >= 14 && age < 18;
  const isYoung = age !== null && age >= 12 && age < 14;   // needs a parent's signed form
  const [parent, setParent] = useState({ name: '', email: '' });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (error) setError('');
  };

  const validateForm = () => {
    if (!formData.full_name.trim()) { setError('Please enter your full name'); return false; }
    if (!formData.email.trim()) { setError('Please enter your email'); return false; }
    if (formData.password.length < 6) { setError('Password must be at least 6 characters'); return false; }
    if (formData.password !== formData.confirmPassword) { setError('Passwords do not match'); return false; }
    if (!formData.partner_code.trim()) { setError('An access code is required to register. Contact your organization or email partnerships@freehappyone.com.'); return false; }
    if (ageBlocked()) { setError('We are not able to create an account at this time.'); return false; }
    if (!formData.birthdate || age === null) { setError('Please enter your date of birth'); return false; }
    if (age < 12) { try { sessionStorage.setItem('fho_age_block', '1'); } catch (e) {} setError('We are not able to create an account at this time.'); return false; }
    if (isYoung && (!parent.name.trim() || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(parent.email.trim()))) { setError("Please enter your parent or guardian's name and email address."); return false; }
    if (isYoung && parent.email.trim().toLowerCase() === formData.email.trim().toLowerCase()) { setError("Your parent or guardian's email must be different from yours."); return false; }
    if (isMinor && !guardianOk) { setError('A parent or legal guardian must give permission before you can register.'); return false; }
    if (!agreedToTerms) { setError('Please agree to the Terms of Service and Privacy Policy'); return false; }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!validateForm()) return;
    setIsLoading(true);
    try {
      const response = await API.post(endpoints.register, {
        email: formData.email,
        password: formData.password,
        full_name: formData.full_name,
        birthdate: formData.birthdate,
        guardian_consent: guardianOk,
        parent_name: isYoung ? parent.name.trim() : null,
        parent_email: isYoung ? parent.email.trim() : null,
        gender: formData.gender || null,
        referral_source: formData.referral_source || null,
        partner_code: formData.partner_code.trim().toUpperCase(),
        timezone: formData.timezone
      });

      if (response.data.pending_parent_consent) {
        navigate('/login', { state: { message: response.data.message } });
        return;
      }
      if (response.data.access_token) {
        localStorage.setItem('token', response.data.access_token);
        if (response.data.user) localStorage.setItem('user', JSON.stringify(response.data.user));
        await syncAccountPartner();
        navigate('/select-home-app');
      } else {
        navigate('/login', { state: { message: 'Registration successful! Please sign in.' } });
      }
    } catch (err) {
      if (err.response) {
        const detail = err.response.data?.detail;
        if (detail && typeof detail === 'object') {
          setError(detail.message || 'Registration failed');
        } else {
          setError(detail || err.response.data?.message || 'Registration failed');
        }
      }
      else if (err.request) setError('Cannot connect to server.');
      else setError('An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.bgMesh} />
      <div style={styles.card}>
        <div style={styles.brandRow}>
          <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="18" cy="18" r="16" stroke="#059669" strokeWidth="2.5" fill="none"/>
            <path d="M18 8 A10 10 0 1 1 8 18" stroke="#ef4444" strokeWidth="2.5" strokeLinecap="round" fill="none"/>
            <circle cx="18" cy="18" r="4" fill="#4f46e5"/>
          </svg>
          <span style={styles.brandName}>
            <span style={{color:'#4f46e5'}}>F</span>
            <span style={{color:'#ef4444'}}>H</span>
            <span style={{color:'#059669'}}>O</span>
            {' PLATFORM'}
          </span>
        </div>

        <h1 style={styles.heading}>Begin Your Journey</h1>
        <p style={styles.subheading}>Create your account and start transforming</p>

        {error && <div style={{...styles.alert, ...styles.alertError}}>{error}</div>}

        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.fieldGroup}>
            <label style={styles.label}>Full Name</label>
            <input type="text" name="full_name" value={formData.full_name} onChange={handleChange}
              placeholder="John Doe" required disabled={isLoading} style={styles.input}
              onFocus={e => e.target.style.borderColor = '#4f46e5'}
              onBlur={e => e.target.style.borderColor = 'rgba(30,58,95,0.2)'} />
          </div>
          <div style={styles.fieldGroup}>
            <label style={styles.label}>Email Address</label>
            <input type="email" name="email" value={formData.email} onChange={handleChange}
              placeholder="you@example.com" required disabled={isLoading} style={styles.input}
              onFocus={e => e.target.style.borderColor = '#4f46e5'}
              onBlur={e => e.target.style.borderColor = 'rgba(30,58,95,0.2)'} />
          </div>
          <div style={styles.fieldGroup}>
            <label style={styles.label}>Password</label>
            <input type="password" name="password" value={formData.password} onChange={handleChange}
              placeholder="Create a password (min. 6 characters)" required disabled={isLoading} style={styles.input}
              onFocus={e => e.target.style.borderColor = '#4f46e5'}
              onBlur={e => e.target.style.borderColor = 'rgba(30,58,95,0.2)'} />
          </div>
          <div style={styles.fieldGroup}>
            <label style={styles.label}>Confirm Password</label>
            <input type="password" name="confirmPassword" value={formData.confirmPassword} onChange={handleChange}
              placeholder="Confirm your password" required disabled={isLoading} style={styles.input}
              onFocus={e => e.target.style.borderColor = '#4f46e5'}
              onBlur={e => e.target.style.borderColor = 'rgba(30,58,95,0.2)'} />
          </div>

          <div style={styles.fieldGroup}>
            <label style={styles.label}>Access Code <span style={{color:'#ef4444'}}>*</span></label>
            <input type="text" name="partner_code" value={formData.partner_code} onChange={handleChange}
              placeholder="Enter your organization or partner code"
              required disabled={isLoading} style={{...styles.input, fontFamily: 'monospace', letterSpacing: '0.08em', textTransform: 'uppercase'}}
              onFocus={e => e.target.style.borderColor = '#4f46e5'}
              onBlur={e => e.target.style.borderColor = 'rgba(30,58,95,0.2)'} />
            <span style={{fontSize:'11px', color:'rgba(30,58,95,0.5)', marginTop:'4px'}}>
              Provided by your organization, employer, or FHO partner. Don't have one? Email <a href="mailto:partnerships@freehappyone.com" style={{color:'#4f46e5'}}>partnerships@freehappyone.com</a>
            </span>
          </div>

          <div style={styles.fieldGroup}>
            <label style={styles.label}>Date of Birth <span style={{color:'#ef4444'}}>*</span></label>
            <input type="date" name="birthdate" value={formData.birthdate} onChange={handleChange}
              max={new Date().toISOString().slice(0, 10)}
              required disabled={isLoading} style={styles.input} />
            <span style={{fontSize:'11px', color:'rgba(30,58,95,0.5)', marginTop:'4px'}}>
              We keep only your age group, not your date of birth.
            </span>
          </div>

          {isYoung && (
            <div style={{ ...styles.optionalBox, background: 'rgba(79,70,229,0.06)', border: '1px solid rgba(79,70,229,0.25)' }}>
              <p style={{ margin: '0 0 10px', fontSize: '14px', color: '#1e3a5f', lineHeight: 1.5 }}>
                Because you are under 14, a parent or guardian must give written permission. We will email them a short form to sign. Your account opens after we receive it.
              </p>
              <div style={styles.fieldGroup}>
                <label style={styles.label}>Parent or guardian's name</label>
                <input type="text" value={parent.name} maxLength={80} disabled={isLoading} style={styles.input}
                  onChange={e => { setParent({ ...parent, name: e.target.value }); if (error) setError(''); }} />
              </div>
              <div style={{ ...styles.fieldGroup, marginTop: '12px' }}>
                <label style={styles.label}>Parent or guardian's email</label>
                <input type="email" value={parent.email} maxLength={120} disabled={isLoading} style={styles.input}
                  onChange={e => { setParent({ ...parent, email: e.target.value }); if (error) setError(''); }} />
              </div>
            </div>
          )}

          {isMinor && (
            <div style={styles.termsRow}>
              <input type="checkbox" id="guardian-checkbox" checked={guardianOk}
                onChange={(e) => setGuardianOk(e.target.checked)}
                style={{width:'18px', height:'18px', cursor:'pointer', accentColor:'#4f46e5', flexShrink:0, marginTop:'2px'}} />
              <label htmlFor="guardian-checkbox" style={styles.termsText}>
                My parent or legal guardian has reviewed the Terms of Service and gives permission for me to use this platform.
              </label>
            </div>
          )}

          <div style={styles.optionalBox}>
            <p style={styles.optionalLabel}>Help us personalize your experience (optional)</p>
            <div style={{...styles.fieldGroup, marginTop: '12px'}}>
              <label style={styles.label}>Gender</label>
              <select name="gender" value={formData.gender} onChange={handleChange} disabled={isLoading} style={styles.select}>
                <option value="">Select gender</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="prefer-not-to-say">Prefer not to say</option>
              </select>
            </div>
            <div style={{...styles.fieldGroup, marginTop: '12px'}}>
              <label style={styles.label}>How did you hear about us?</label>
              <select name="referral_source" value={formData.referral_source} onChange={handleChange} disabled={isLoading} style={styles.select}>
                <option value="">Select an option</option>
                <option value="hcsm">Health Care Sharing Ministry</option>
                <option value="ebs">Employee Benefits Specialist</option>
                <option value="hrm">HR / Human Resources</option>
                <option value="insureco">Insurance Company</option>
                <option value="peo">Professional Employer Organization (PEO)</option>
                <option value="rehab">Rehabilitation Facility</option>
                <option value="chaplain">Corporate Chaplaincy</option>
                <option value="ltc">Long-Term Care Facility</option>
                <option value="influencer">Social Media Influencer / Creator</option>
                <option value="mindfulness">Mindfulness Certification Program</option>
                <option value="nonprofit">Faith-Based Nonprofit</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>

          <div style={styles.termsRow}>
            <input type="checkbox" id="terms-checkbox" checked={agreedToTerms}
              onChange={(e) => setAgreedToTerms(e.target.checked)}
              style={{width:'18px', height:'18px', cursor:'pointer', accentColor:'#4f46e5', flexShrink:0, marginTop:'2px'}} />
            <label htmlFor="terms-checkbox" style={styles.termsText}>
              I agree to the <a href="/terms" target="_blank" rel="noopener noreferrer" style={styles.termsLink}>Terms of Service</a> and <a href="/privacy" target="_blank" rel="noopener noreferrer" style={styles.termsLink}>Privacy Policy</a>
            </label>
          </div>

          <button type="submit" disabled={isLoading}
            style={{...styles.btn, ...(isLoading ? styles.btnDisabled : {})}}
            onMouseEnter={e => { if (!isLoading) e.target.style.background = '#4338ca'; }}
            onMouseLeave={e => { if (!isLoading) e.target.style.background = '#4f46e5'; }}>
            {isLoading ? 'Creating account...' : 'Create Account'}
          </button>
        </form>

        <div style={styles.divider}>
          <span style={styles.dividerLine} />
          <span style={styles.dividerText}>already a member?</span>
          <span style={styles.dividerLine} />
        </div>

        <Link to="/login" style={styles.loginLink}>Sign in to your account</Link>

        <p style={styles.tagline}>Wisdom · Mental wellness · Family coaching</p>
      </div>

      <style>{`
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        input::placeholder { color: rgba(30,58,95,0.35); }
        select option { background: #fff; color: #1e3a5f; }
      `}</style>
    </div>
  );
};

const styles = {
  page: {
    minHeight: '100vh',
    background: '#5b9bd5',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '24px 16px',
    position: 'relative',
    overflow: 'hidden',
    fontFamily: "'Georgia', 'Times New Roman', serif",
  },
  bgMesh: {
    position: 'absolute', inset: 0,
    background: `radial-gradient(ellipse 70% 50% at 15% 10%, rgba(255,255,255,0.25) 0%, transparent 55%),
                 radial-gradient(ellipse 50% 40% at 85% 85%, rgba(30,58,95,0.2) 0%, transparent 50%)`,
    pointerEvents: 'none',
  },
  card: {
    position: 'relative',
    width: '100%',
    maxWidth: '440px',
    background: 'rgba(255,255,255,0.75)',
    border: '1px solid rgba(255,255,255,0.9)',
    borderRadius: '20px',
    padding: '40px 36px',
    backdropFilter: 'blur(20px)',
    boxShadow: '0 20px 60px rgba(30,58,95,0.18), 0 4px 16px rgba(30,58,95,0.1)',
    animation: 'fadeUp 0.45s ease forwards',
  },
  brandRow: { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '24px' },
  brandName: { fontSize: '13px', fontWeight: '700', color: '#1e3a5f', letterSpacing: '0.1em', textTransform: 'uppercase' },
  heading: { margin: '0 0 6px', fontSize: '26px', fontWeight: '700', color: '#1e3a5f', letterSpacing: '-0.02em', lineHeight: 1.2 },
  subheading: { margin: '0 0 20px', fontSize: '14px', color: 'rgba(30,58,95,0.6)' },
  alert: { padding: '10px 14px', borderRadius: '8px', fontSize: '13px', marginBottom: '16px', lineHeight: 1.5 },
  alertError: { background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', color: '#991b1b' },
  form: { display: 'flex', flexDirection: 'column', gap: '14px' },
  fieldGroup: { display: 'flex', flexDirection: 'column', gap: '5px' },
  label: { fontSize: '11px', fontWeight: '700', color: 'rgba(30,58,95,0.6)', letterSpacing: '0.08em', textTransform: 'uppercase' },
  input: {
    background: 'rgba(255,255,255,0.8)', border: '1px solid rgba(30,58,95,0.2)', borderRadius: '10px',
    padding: '11px 14px', fontSize: '15px', color: '#1e3a5f', outline: 'none', transition: 'border-color 0.2s',
    fontFamily: "'Georgia', serif", width: '100%', boxSizing: 'border-box',
  },
  select: {
    background: 'rgba(255,255,255,0.8)', border: '1px solid rgba(30,58,95,0.2)', borderRadius: '10px',
    padding: '11px 14px', fontSize: '14px', color: '#1e3a5f', outline: 'none',
    width: '100%', boxSizing: 'border-box', cursor: 'pointer', fontFamily: "'Georgia', serif",
  },
  optionalBox: { background: 'rgba(30,58,95,0.04)', border: '1px solid rgba(30,58,95,0.1)', borderRadius: '12px', padding: '16px' },
  optionalLabel: { fontSize: '12px', color: 'rgba(30,58,95,0.5)', textAlign: 'center', marginBottom: '12px', marginTop: 0 },
  termsRow: { display: 'flex', alignItems: 'flex-start', gap: '10px' },
  termsText: { fontSize: '13px', color: 'rgba(30,58,95,0.75)', lineHeight: 1.6, cursor: 'pointer' },
  termsLink: { color: '#4f46e5', textDecoration: 'underline' },
  btn: {
    background: '#4f46e5', color: '#fff', border: 'none', borderRadius: '10px', padding: '13px',
    fontSize: '15px', fontWeight: '600', cursor: 'pointer', transition: 'background 0.2s',
    fontFamily: "'Georgia', serif", letterSpacing: '0.02em', width: '100%',
  },
  btnDisabled: { background: 'rgba(79,70,229,0.5)', cursor: 'not-allowed' },
  divider: { display: 'flex', alignItems: 'center', gap: '10px', margin: '18px 0 14px' },
  dividerLine: { flex: 1, height: '1px', background: 'rgba(30,58,95,0.12)' },
  dividerText: { fontSize: '11px', color: 'rgba(30,58,95,0.4)', letterSpacing: '0.06em' },
  loginLink: {
    display: 'block', textAlign: 'center', padding: '12px', borderRadius: '10px',
    border: '1px solid rgba(79,70,229,0.35)', color: '#4f46e5', fontSize: '14px',
    textDecoration: 'none', fontFamily: "'Georgia', serif", fontWeight: '600',
  },
  tagline: { textAlign: 'center', fontSize: '11px', color: 'rgba(30,58,95,0.35)', marginTop: '20px', marginBottom: 0, letterSpacing: '0.04em' },
};

export default Register;
